import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import objectId from 'bson-objectid';
/* actions */
import { getAllDepartments } from '../../redux/actions/departments';
import { getAllPABXs } from '../../redux/actions/pabxs';
import { addDevices } from '../../redux/actions/devices';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
/* form-validation */
import schema from '../../form-validation/device';
/* utils */
import handleChangeDefault from '../../utils/handleChangeDefault';

function Add () {
  const INITIAL_VALUES = {
    name: 'no name',
    initials: 'GRP',
    type: '',
    department: '',
    pabx: '',
    numbers: []
  };
  const INITIAL_RANDOM_DEVICES = [
    {
      id: objectId().str,
      number: ''
    }
  ];
  const INITIAL_INTERVAL_DEVICES = {
    initial: '',
    final: ''
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors, reset } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const pabxs = useSelector(state => state.pabxs);
  const devices = useSelector(state => state.devices);
  const departments = useSelector(state => state.departments);
  const [data, setData] = useState(INITIAL_VALUES);
  const [typeRegister, setTypeRegister] = useState('R');
  const [randomDevices, setRandomDevices] = useState(INITIAL_RANDOM_DEVICES);
  const [intervalDevices, setIntervalDevices] = useState(
    INITIAL_INTERVAL_DEVICES
  );

  useEffect(() => {
    dispatch(getAllDepartments());
    dispatch(getAllPABXs());
  }, [dispatch]);

  const submit = () => {
    const resetForm = () => {
      reset();
      setData({ ...INITIAL_VALUES });
      setRandomDevices([...INITIAL_RANDOM_DEVICES]);
      setIntervalDevices({ ...INITIAL_INTERVAL_DEVICES });
      setTypeRegister('R');
    };
    const newData = { ...data };
    if (data.type !== 'G') {
      delete newData.initials;
    }
    if (data.type !== 'G' && data.type !== 'E' && data.type !== 'L') {
      delete newData.name;
    }
    if (typeRegister === 'R') {
      newData.numbers = randomDevices.reduce((newArray, currentItem) => {
        if (!newArray.includes(currentItem.number)) {
          newArray.push(currentItem.number);
        }
        return newArray;
      }, []);
    } else {
      const numbers = [];
      const initial = parseInt(intervalDevices.initial, 10);
      const final = parseInt(intervalDevices.final, 10);
      for (let i = initial; i <= final; i++) {
        numbers.push(String(i));
      }
      newData.numbers = [...numbers];
    }
    dispatch(addDevices(newData, resetForm, submitButton.current));
  };

  const handleClickAddNumberDevice = () => {
    const numbers = [...randomDevices];
    numbers.push({ number: '', id: objectId().str });
    setRandomDevices([...numbers]);
  };

  const handleClickRemoveNumberDevice = position => {
    const devices = [...randomDevices];
    if (devices.length > 1) {
      devices.splice(position, 1);
      setRandomDevices([...devices]);
    }
  };

  const handleChangeRandomNumberDevice = (evt, position) => {
    evt.preventDefault();
    const { value } = evt.target;
    if (value.match(/^\d+$/) || value === '') {
      const devices = [...randomDevices];
      devices[position].number = value;
      setRandomDevices([...devices]);
    } else {
      evt.target.value = randomDevices[position].number;
    }
  };

  const handleChangeIntervalNumberDevice = evt => {
    evt.preventDefault();
    const { value, name } = evt.target;
    if (value.match(/^\d+$/) || value === '') {
      setIntervalDevices({ ...intervalDevices, [name]: value });
    } else {
      evt.target.value = intervalDevices[name];
    }
  };

  return (
    <div className="container-fluid">
      {devices.addSuccess && (
        <AlertMsg type="success" msg="Dispositivo cadastrado com sucesso" />
      )}
      {devices.errorContent &&
        !devices.errorContent.message.includes('400') &&
        devices.addError && (
        <AlertMsg type="danger" msg="Falha durante cadastro(s)" />
      )}
      {devices.errorContent &&
        devices.errorContent.message.includes('400') &&
        devices.addError && (
        <AlertMsg
          type="danger"
          msg="Um ou mais dispositivos informados já estão cadastrados"
        />
      )}
      {!devices.addSuccess && !devices.addError && (
        <AlertMsg type="info" msg="Área cadastro de dispositivos" />
      )}
      <FormHeader title="Cadastrar Dispositivo" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="form-group">
          <label htmlFor="type">
            Tipo<span className="field-required">*</span>
          </label>
          <select
            className="form-control"
            id="type"
            name="type"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          >
            <option value="">-- Selecione --</option>
            <option value="E">Extensão</option>
            <option value="G">Grupo</option>
            <option value="N">Tronco</option>
            <option value="U">OGM</option>
            <option value="L">Linha</option>
          </select>
          {errors.type && errors.type.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        {(data.type === 'E' || data.type === 'G' || data.type === 'L') && (
          <div className="form-group">
            <label htmlFor="name">
              Nome<span className="field-required">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              autoComplete="off"
              onChange={evt => handleChangeDefault(evt, data, setData)}
              defaultValue="no name"
              ref={register}
            />
            {errors.name && errors.name.type === 'required' && (
              <ErrorMsgField type="required" />
            )}
          </div>
        )}
        {data.type === 'G' && (
          <div className="form-group">
            <label htmlFor="initials">
              Sigla<span className="field-required">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="initials"
              name="initials"
              autoComplete="off"
              onChange={evt => handleChangeDefault(evt, data, setData)}
              defaultValue="GRP"
              ref={register}
            />
            {errors.initials && errors.initials.type === 'required' && (
              <ErrorMsgField type="required" />
            )}
            {errors.initials && errors.initials.type === 'max' && (
              <ErrorMsgField type="max" />
            )}
          </div>
        )}
        <div className="form-group">
          <label htmlFor="department">
            Departamento<span className="field-required">*</span>
          </label>
          <select
            className="form-control"
            id="department"
            name="department"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          >
            <option value="">-- Selecione --</option>
            {departments.data.map((dpt, i) => {
              const _id = dpt._id || Date.now() + '' + i;
              return (
                <option key={_id} value={_id}>
                  {dpt.name}
                </option>
              );
            })}
          </select>
          {errors.department && errors.department.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="pabx">
            PABX<span className="field-required">*</span>
          </label>
          <select
            className="form-control"
            id="pabx"
            name="pabx"
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          >
            <option value="">-- Selecione --</option>
            {pabxs.data.map((pabx, i) => {
              const _id = pabx._id || Date.now() + '' + i;
              return (
                <option key={_id} value={_id}>
                  {pabx.name} | {pabx.ip}
                </option>
              );
            })}
          </select>
          {errors.pabx && errors.pabx.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="typeRegister">
            Tipo de Cadastro<span className="field-required">*</span>
          </label>
          <select
            className="form-control"
            id="typeRegister"
            name="typeRegister"
            defaultValue={typeRegister}
            onChange={evt => setTypeRegister(evt.target.value)}
            ref={register}
          >
            <option value="R">Aleatório</option>
            <option value="I">Intervalo</option>
          </select>
        </div>
        {typeRegister === 'R' && (
          <div className="pb-2">
            <div className="form-group">
              <button
                type="button"
                className="btn btn-dark"
                onClick={handleClickAddNumberDevice}
              >
                <i className="fa fa-plus"></i>&nbsp; Adicionar Número
              </button>
            </div>
            {randomDevices.map((device, position) => {
              return (
                <div
                  className="row pt-2"
                  key={device.id}
                  style={{
                    backgroundColor: position % 2 === 0 ? '#eee' : '#fff'
                  }}
                >
                  <div className="col-sm-9">
                    <div className="form-group mb-1">
                      <label htmlFor={`number_${position}`}>
                        Número<span className="field-required">*</span>
                      </label>
                      <input
                        type="text"
                        autoComplete="off"
                        className="form-control input-number"
                        id={`number_${position}`}
                        name={`number_${position}`}
                        onChange={evt =>
                          handleChangeRandomNumberDevice(evt, position)
                        }
                        defaultValue={device.number}
                        ref={register}
                      />
                      {errors[`number_${position}`] &&
                        errors[`number_${position}`].type === 'integer' && (
                        <ErrorMsgField type="integer" />
                      )}
                      {errors[`number_${position}`] &&
                        errors[`number_${position}`].type === 'repeat' && (
                        <ErrorMsgField type="repeat" />
                      )}
                      {(!errors[`number_${position}`] ||
                        (errors[`number_${position}`] &&
                          errors[`number_${position}`].type === '')) && (
                        <small className="form-text m-0">&nbsp;</small>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-3">
                    <div>
                      <button
                        type="button"
                        className="btn btn-danger btn-block btn-remove-recess rounded-0"
                        onClick={() => handleClickRemoveNumberDevice(position)}
                      >
                        <i className="fa fa-trash-alt"></i>
                      </button>
                      <small className="form-text m-0">&nbsp;</small>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {typeRegister === 'I' && (
          <div className="row">
            <div className="col-sm-6">
              <div className="form-group">
                <label htmlFor="initial">
                  Número Inicial<span className="field-required">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="initial"
                  name="initial"
                  autoComplete="off"
                  onChange={handleChangeIntervalNumberDevice}
                  ref={register}
                />
                {errors.initial && errors.initial.type === 'required' && (
                  <ErrorMsgField type="required" />
                )}
                {errors.initial && errors.initial.type === 'start' && (
                  <ErrorMsgField type="start" />
                )}
              </div>
            </div>
            <div className="col-sm-6">
              <div className="form-group">
                <label htmlFor="final">
                  Número Final<span className="field-required">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="final"
                  name="final"
                  autoComplete="off"
                  onChange={handleChangeIntervalNumberDevice}
                  ref={register}
                />
                {errors.final && errors.final.type === 'required' && (
                  <ErrorMsgField type="required" />
                )}
              </div>
            </div>
          </div>
        )}
        <FormFooter url="/devices" reference={submitButton} />
      </form>
    </div>
  );
}

export default Add;
